import React from "react";

import {
  DeleteRounded,
  HideImageRounded,
  ReportRounded,
  SettingsApplicationsRounded,
} from "@mui/icons-material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FavoriteIcon from "@mui/icons-material/FavoriteRounded";
import SendIcon from "@mui/icons-material/Send";
import { Box, ButtonBase } from "@mui/material";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Collapse from "@mui/material/Collapse";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import { DateTime } from "luxon";
import { nanoid } from "nanoid";
import { useNavigate } from "react-router-dom";

import useAuth from "../../config/context/authContext";
import useMainState from "../../config/context/mainStateContext";
import useModal from "../../config/context/modalContext";
import { SpottService } from "../../config/services/SpottService";
import { GreyPallet } from "../../config/styles";
import { Dev } from "../../config/util/consoleUtil";
import { UtilComponents } from "../../config/util/utilComponents";

import TextMain from "../InputMain/TextMain";
import MainText from "../MainText";
import ModalReportCreate from "../ModalReportCreate";

import { SpottCommentType, SpottPostType } from "../../types/database";

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

interface ComponentProps {
  item: SpottPostType;
}

export default function SpottPost({ item }: ComponentProps) {
  const navigate = useNavigate();
  const { currentUserInfo, currentUserState } = useAuth();
  const { pushAlert, showImage } = useModal();
  const { userEnableToInteract } = useMainState();

  const LikeAdd = SpottService.UpdateArrayAdd();
  const LikeRemove = SpottService.UpdateArrayRemove();

  const CommentAdd = SpottService.UpdateArrayAdd();
  const CommentRemove = SpottService.UpdateArrayRemove();

  const [expanded, setExpanded] = React.useState(false);

  const [showModalReport, setShowModalReport] = React.useState<
    false | { postId?: string; commentId?: string }
  >(false);

  const [likes, setLikes] = React.useState(item.likes ?? []);
  const [comments, setComments] = React.useState(item.comments ?? []);

  const [commentField, setCommentField] = React.useState("");

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleLike = async () => {
    try {
      if (!userEnableToInteract()) return;

      if (likes.includes(String(currentUserState?.uid))) {
        await LikeRemove.runRequest(String(item.id), "likes", String(currentUserState?.uid));
        setLikes(likes.filter((like) => like !== String(currentUserState?.uid)));
      } else {
        await LikeAdd.runRequest(String(item.id), "likes", String(currentUserState?.uid));
        setLikes([...likes, String(currentUserState?.uid)]);
      }
    } catch (error) {
      console.log(...new Dev("handleLike", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao curtir a postagem.",
      });
    }
  };

  const handleComment = async () => {
    try {
      if (!userEnableToInteract()) return;

      const dateNow = new Date();

      const commentPayload = {
        id: nanoid(),
        createdAtTimestamp: dateNow.getTime(),
        createdAtISO: dateNow.toISOString(),
        text: commentField,
        userId: String(currentUserState?.uid),
        userColor: String(currentUserInfo?.color),
        userName: String(currentUserInfo?.name),
      };

      await CommentAdd.runRequest(String(item.id), "comments", commentPayload);

      setComments([...comments, commentPayload]);

      setCommentField("");
    } catch (error) {
      console.log(...new Dev("handleComment", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao comentar postagem.",
      });
    }
  };

  const handleDeleteComment = async (commentPayload: SpottCommentType) => {
    try {
      pushAlert({
        isConfirm: true,
        type: "delete",
        onConfirm: async () => {
          console.log(...new Dev("handleDeleteComment", item?.id, commentPayload).Info());
          await CommentRemove.runRequest(String(item.id), "comments", commentPayload);

          setComments(
            comments.filter(
              (comment) =>
                !(
                  comment.createdAtTimestamp === commentPayload.createdAtTimestamp &&
                  comment.userId === commentPayload.userId
                )
            )
          );
        },
      });
    } catch (error) {
      console.log(...new Dev("handleDeleteComment", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao remover comentário.",
      });
    }
  };

  return (
    <Card sx={{ mt: 2, boxShadow: "none", border: `3px solid ${GreyPallet.Light}` }}>
      {item?.files?.length > 0
        ? item?.files.map((itemImage) =>
            itemImage.deleted ? (
              <Box
                key={itemImage.ref}
                style={{
                  backgroundColor: GreyPallet.Light,
                  color: GreyPallet.Regular,
                  fontSize: 14,
                }}
                p={2}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <HideImageRounded sx={{ mr: 1, fontSize: 18 }} />
                Imagem Removida
              </Box>
            ) : (
              <CardMedia
                onClick={() => showImage(itemImage.resource)}
                key={itemImage.ref}
                component="img"
                height="194"
                image={itemImage.resource}
                alt="Imagem Enviada"
              />
            )
          )
        : null}

      {showModalReport ? (
        <ModalReportCreate reportInfo={showModalReport} onClose={() => setShowModalReport(false)} />
      ) : null}

      <CardContent sx={{ pb: 0 }}>
        <Box>
          <MainText size="small" color="regular">
            {DateTime.fromMillis(item?.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
          </MainText>
        </Box>
        <Box sx={{ mt: 1 }}>
          <MainText size="body">{item?.text}</MainText>
        </Box>
      </CardContent>
      <CardActions disableSpacing sx={{ pt: 1 }} style={{ color: GreyPallet.Regular }}>
        <IconButton
          style={{ color: GreyPallet.Regular }}
          disabled={LikeAdd.isLoading || LikeRemove.isLoading}
          onClick={handleLike}
        >
          <FavoriteIcon
            color={likes.includes(String(currentUserState?.uid)) ? "error" : "inherit"}
          />
        </IconButton>
        <MainText size="caption" color="regular">
          {likes?.length}
        </MainText>

        <IconButton
          style={{ color: GreyPallet.Regular, marginLeft: 10 }}
          disabled={LikeAdd.isLoading || LikeRemove.isLoading}
          onClick={handleExpandClick}
        >
          <ChatBubbleIcon color="inherit" />
        </IconButton>
        <MainText size="caption" color="regular">
          {comments?.length}
        </MainText>

        <IconButton
          style={{ color: GreyPallet.Regular, marginLeft: 10 }}
          onClick={() => setShowModalReport({ postId: item?.id })}
        >
          <ReportRounded color="inherit" />
        </IconButton>

        {UtilComponents.isStaff(currentUserState) ? (
          <IconButton
            style={{ color: GreyPallet.Regular, marginLeft: 10 }}
            onClick={() => navigate("/post/" + item.id)}
          >
            <SettingsApplicationsRounded color="inherit" />
          </IconButton>
        ) : null}

        <ExpandMore expand={expanded} onClick={handleExpandClick} aria-expanded={expanded}>
          <ExpandMoreIcon />
        </ExpandMore>
      </CardActions>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ paddingBottom: "0px !important" }}>
          {comments.length > 0 ? (
            comments.map((comment, index) => (
              <Box
                key={`${comment.createdAtTimestamp}-${index}`}
                sx={{
                  mb: 2,
                  pt: 1,
                  pb: 2,
                  px: 2,
                  backgroundColor:
                    comment.userId === currentUserState?.uid ? GreyPallet.Light : undefined,
                  border:
                    comment.userId === currentUserState?.uid
                      ? undefined
                      : `3px solid ${GreyPallet.Light}`,
                  borderRadius: "15px",
                }}
              >
                <Box
                  style={{
                    color: GreyPallet.Regular,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box
                    style={{
                      color: GreyPallet.Regular,
                      display: "flex",
                      justifyContent: "start",
                      alignItems: "center",
                      marginBottom: 4,
                    }}
                  >
                    <ButtonBase
                      onClick={() =>
                        UtilComponents.isStaff(currentUserState)
                          ? navigate("/user/" + comment.userId)
                          : null
                      }
                      sx={{ mr: 1, borderRadius: "50%" }}
                    >
                      <AccountCircleIcon
                        fontSize="inherit"
                        sx={{ color: comment?.userColor || GreyPallet.Regular }}
                      />
                    </ButtonBase>
                    <MainText size="small" color="regular">
                      {comment?.userName}
                      {" - "}
                      {DateTime.fromMillis(comment?.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
                    </MainText>
                  </Box>

                  <Box style={{ marginLeft: 10 }}>
                    {comment.userId === currentUserState?.uid ? (
                      <IconButton
                        style={{ color: GreyPallet.Regular, fontSize: 18 }}
                        onClick={() => handleDeleteComment(comment)}
                      >
                        <DeleteRounded color="inherit" fontSize="inherit" />
                      </IconButton>
                    ) : (
                      <IconButton
                        style={{ color: GreyPallet.Regular, fontSize: 18 }}
                        onClick={() =>
                          setShowModalReport({ postId: item?.id, commentId: comment.id })
                        }
                      >
                        <ReportRounded color="inherit" fontSize="inherit" />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                <MainText size="caption" color="black">
                  {comment?.text}
                </MainText>
              </Box>
            ))
          ) : (
            <Box mb={4}>
              <MainText size="caption" color="regular">
                Nenhum comentário...
              </MainText>
            </Box>
          )}

          {/*  */}

          <TextMain
            name="Comentar"
            value={commentField}
            onChange={setCommentField}
            textColor={"regular"}
            adornmentIcon={<SendIcon />}
            adornmentClick={handleComment}
            adornmentDisabled={CommentAdd.isLoading}
          />
        </CardContent>
      </Collapse>
    </Card>
  );
}
